'use client';

import { Button } from '@shared/components/shadcn';
import { ROUTES, useIsMobile } from '@shared/utils';
import { useTranslations } from 'next-intl';
import Link from 'next/link';
import type { ComponentProps } from 'react';
import { benefitButtonClasses, benefitTitleClasses, benefitWrapperClasses, type BenefitsSectionDensity } from '../../styles';

type BenefitContainerProps = ComponentProps<'section'> & {
  title: string;
  children: React.ReactNode;
  namespace?: string;
  density?: BenefitsSectionDensity;
};

/**
 * A section component for benefits with title, description, children content and a "Join now" button.
 *
 * @param title - The main title
 * @param children - Content to render between description and button
 * @param className - Additional CSS classes
 * @param density - Vertical spacing preset for the section. Defaults to `default`.
 */
function BenefitContainer({ title, children, namespace = '', className, density, ...props }: BenefitContainerProps) {
  const t = useTranslations(namespace);
  const isMobile = useIsMobile();

  return (
    <section className={className} data-testid="benefit-container" data-track-container="Discover Benefits" {...props}>
      <div className={benefitWrapperClasses({ density })}>
        <h2 className={benefitTitleClasses({ density })} data-testid="benefit-container-title">
          {title}
        </h2>
        <div className="mb-10" data-testid="benefit-container-content" data-track-container="Navigation List">
          {children}
        </div>
        <Button asChild data-testid="benefit-container-button" className={benefitButtonClasses({ density })} data-track-name="Register" size={isMobile ? 'l' : 'm'}>
          <Link href={ROUTES.REGISTER}>{t('join_now')}</Link>
        </Button>
      </div>
    </section>
  );
}

export { BenefitContainer, type BenefitContainerProps };
